import { postTrackingData } from "@/api/endpoints/track";
import { MENTIONS_SHEET_DATE_FORMAT } from "@/constants";
import { useInsightView } from "@/hooks/useInsightView";
import { omitPageParams } from "@/hooks/usePageParam";
import { EVENT } from "@/tracking_events";
import { Area, AreaChart, CartesianGrid, XAxis } from "recharts";
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from "@/components/ui/chart";
import {
  Widget,
  WidgetContent,
  WidgetHeader,
  WidgetProps,
  WidgetStat,
  WidgetTitle,
} from "@components/ui/widget";
import { Trans, useLingui } from "@lingui/react/macro";
import { plural } from "@lingui/core/macro";
import { useQuery } from "@tanstack/react-query";
import {
  getMCPMonitorGrowthRate,
  getMCPMonitorTimeSeries,
  getMCPMonitorTotals,
} from "@/api/endpoints/searches";
import { SearchFilterV4WithDates } from "@/types/SearchFilter";
import { useI18n } from "@/contexts/i18nContext";
import {
  getMCPMonitorTimeSeriesGranularityFromDayDiff,
  getMCPMonitorFiltersFromSearchFilterV4,
  formatDateFromMCPMonitorTimeSeriesGranularity,
  getPeriodFromStartDateAndGranularity,
} from "./utils";
import { generatePath, Link, useHistory, useLocation } from "react-router-dom";
import { subDays } from "date-fns";
import { formatDateForApi } from "@/utils";
import useTimezone from "@/hooks/useActiveTimezone";
import { ArrowRightIcon } from "lucide-react";

export default function MentionsOverTimeWidget({
  detailsLink,
  hideTotals,
  diffInDays,
  searchUuid,
  orgUuid,
  filters,
  dayLink,
  ...rest
}: WidgetProps & {
  filters: SearchFilterV4WithDates;
  hideTotals?: boolean;
  detailsLink?: string;
  diffInDays: number;
  searchUuid: Uuid;
  orgUuid: Uuid;
  dayLink: string;
}) {
  const { abbreviateNumber, formatDate } = useI18n();
  const { t } = useLingui();
  const timezone = useTimezone();

  // URL stte
  const history = useHistory();
  const insight = useInsightView();
  const location = useLocation();

  // Server state
  const { data: totalEpisodesData, isLoading: isTotalEpisodesLoading } =
    useQuery({
      queryKey: [searchUuid, "total-episodes", filters, hideTotals],
      queryFn: () =>
        getMCPMonitorTotals(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          metric: "episode_count",
        }),
      enabled: !hideTotals,
    });

  const { data: totalMentionsData, isLoading: isTotalMentionsLoading } =
    useQuery({
      queryKey: [searchUuid, "total-mentions-growth-rate", filters, hideTotals],
      queryFn: () =>
        getMCPMonitorGrowthRate(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          compare_start_date: formatDateForApi(
            subDays(new Date(filters.start_date), diffInDays + 1)
          ),
          compare_end_date: formatDateForApi(
            subDays(new Date(filters.end_date), diffInDays + 1)
          ),
          metric: "mention_count",
        }),
      enabled: !hideTotals,
    });

  const granularity = getMCPMonitorTimeSeriesGranularityFromDayDiff(diffInDays);

  const {
    data: mentionsTimeSeriesData,
    isLoading: isMentionsTimeSeriesLoading,
  } = useQuery({
    queryKey: [searchUuid, "mentions-time-series", filters],
    queryFn: () =>
      getMCPMonitorTimeSeries(searchUuid, {
        ...getMCPMonitorFiltersFromSearchFilterV4(filters),
        metric: "mention_count",
        granularity,
      }),
  });

  const { data: episodeTimeSeriesData, isLoading: isEpisodeTimeSeriesLoading } =
    useQuery({
      queryKey: [searchUuid, "episode-count-time-series", filters],
      queryFn: () =>
        getMCPMonitorTimeSeries(searchUuid, {
          ...getMCPMonitorFiltersFromSearchFilterV4(filters),
          metric: "episode_count",
          granularity,
        }),
    });

  // Helpers
  const isLoading =
    isTotalMentionsLoading ||
    isTotalEpisodesLoading ||
    isMentionsTimeSeriesLoading ||
    isEpisodeTimeSeriesLoading;

  const noMentionsValues =
    mentionsTimeSeriesData?.ok &&
    !mentionsTimeSeriesData.data.points.some((point) => point.value);

  const isEmpty =
    !isLoading &&
    (!mentionsTimeSeriesData?.ok ||
      !episodeTimeSeriesData?.ok ||
      noMentionsValues);

  const chartConfig = {
    mentionCount: {
      label: t`Träffar`,
      color: "var(--color-violet-500)",
    },
  } satisfies ChartConfig;

  return (
    <Widget {...rest}>
      <WidgetHeader
        isLoading={isLoading}
        skeletonClassName={hideTotals ? "h-6" : undefined}
      >
        {detailsLink ? (
          <Link
            to={detailsLink}
            onClick={() => {
              postTrackingData(EVENT.WIDGET_CLICK, {
                insight,
                widget: "mentions_over_time",
                action: "details",
              });
            }}
            className="flex items-center gap-1.5 group/link flex-1"
          >
            <WidgetTitle className="group-hover/link:underline group-hover/link:underline-offset-4">
              <Trans>Träffar över tid</Trans>
            </WidgetTitle>
            <ArrowRightIcon className="size-4 opacity-0 group-hover/link:opacity-100 -translate-x-2 group-hover/link:translate-x-0 transition-all" />
          </Link>
        ) : (
          <WidgetTitle>
            <Trans>Träffar över tid</Trans>
          </WidgetTitle>
        )}
        {!isEmpty && totalMentionsData?.ok && totalEpisodesData?.ok ? (
          <WidgetStat change={totalMentionsData.data.change_pct / 100}>
            {abbreviateNumber(totalMentionsData.data.value, 0)}{" "}
            <span className="text-sm font-medium">
              <Trans>
                i {abbreviateNumber(totalEpisodesData.data.value)}{" "}
                {plural(totalEpisodesData.data.value, {
                  one: "inlägg",
                  other: "inlägg",
                })}
              </Trans>
            </span>
          </WidgetStat>
        ) : null}
      </WidgetHeader>
      <WidgetContent
        isLoading={isLoading}
        className="justify-end"
        isEmpty={isEmpty}
      >
        {mentionsTimeSeriesData?.ok && episodeTimeSeriesData?.ok ? (
          <ChartContainer
            config={chartConfig}
            className="h-full w-full aspect-auto"
          >
            <AreaChart
              accessibilityLayer
              data={mentionsTimeSeriesData.data.points.map((point, i) => ({
                date: point.date,
                mentionCount: point.value,
                episodeCount: episodeTimeSeriesData.data.points[i]?.value ?? 0,
              }))}
              margin={{ bottom: 0, top: 4, left: 4, right: 4 }}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                if (!e.activeLabel || typeof e.activeLabel !== "string") {
                  return;
                }

                const { startDate, endDate } =
                  getPeriodFromStartDateAndGranularity(
                    e.activeLabel,
                    granularity,
                    timezone
                  );

                postTrackingData(EVENT.WIDGET_CLICK, {
                  insight,
                  widget: "mentions_over_time",
                  action: "data_point",
                });

                history.replace({
                  pathname: generatePath(dayLink, {
                    startDate: formatDate(
                      startDate,
                      MENTIONS_SHEET_DATE_FORMAT
                    ),
                    endDate: formatDate(endDate, MENTIONS_SHEET_DATE_FORMAT),
                    uuid: searchUuid,
                    orgUuid,
                  }),
                  search: omitPageParams(location.search, ["sheet_page"]),
                });
              }}
            >
              <defs>
                <linearGradient
                  id="fillMentionCount"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="var(--color-mentionCount)"
                    stopOpacity={0.8}
                  />
                  <stop
                    offset="95%"
                    stopColor="var(--color-mentionCount)"
                    stopOpacity={0.1}
                  />
                </linearGradient>
              </defs>

              <CartesianGrid vertical={false} />
              <XAxis
                dataKey="date"
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) =>
                  formatDateFromMCPMonitorTimeSeriesGranularity(
                    value,
                    granularity,
                    formatDate
                  )
                }
                interval="preserveStartEnd"
              />
              <ChartTooltip
                content={
                  <ChartTooltipContent
                    hideIndicator
                    labelFormatter={(value) =>
                      formatDateFromMCPMonitorTimeSeriesGranularity(
                        value as string,
                        granularity,
                        formatDate
                      )
                    }
                    formatter={(_value, _name, item, index) => {
                      if (index !== 0) return null;

                      const mentionCount = Number(
                        item.payload?.mentionCount ?? 0
                      );
                      const episodeCount = Number(
                        item.payload?.episodeCount ?? 0
                      );

                      return (
                        <span>
                          {abbreviateNumber(mentionCount, 0)}{" "}
                          {plural(mentionCount, {
                            one: "träff",
                            other: "träffar",
                          })}{" "}
                          <Trans>
                            i {abbreviateNumber(episodeCount, 0)}{" "}
                            {plural(episodeCount, {
                              one: "inlägg",
                              other: "inlägg",
                            })}
                          </Trans>
                        </span>
                      );
                    }}
                  />
                }
              />
              <Area
                fill="url(#fillMentionCount)"
                stroke="var(--color-mentionCount)"
                isAnimationActive={false}
                dataKey="mentionCount"
                name="mentionCount"
                type="bump"
              />
            </AreaChart>
          </ChartContainer>
        ) : null}
      </WidgetContent>
    </Widget>
  );
}
